/**
 * What someone sees after signing up without an invitation: their request is
 * in, and nothing else — no sidebar, no data (ADR single-organization, docs/PERMISSIONS.md).
 * The server refuses them everything too; this page only says so kindly.
 *
 * It re-asks the server every 30 seconds, so the moment an admin lets them in
 * the app opens by itself — they also get an email.
 */
import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { statusQuery } from '@/lib/live-data'
import { AuthShell } from '../components/auth-shell'
import { meQueryOptions, useSignOut } from '../use-me'

export function RequestedScreen({
  organizationName,
  email,
}: {
  organizationName: string
  email: string
}) {
  const signOut = useSignOut()
  // Same query as the rest of the app, so when the role changes the shell
  // around every other screen takes over without a reload.
  useQuery(statusQuery(meQueryOptions(), 30_000))

  return (
    <AuthShell
      title="Request sent"
      description={`Your request to join ${organizationName} has been submitted.`}
      footer={`Signed in as ${email}`}
    >
      <div className="flex items-start gap-3 rounded-lg bg-muted px-4 py-3">
        <Clock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          An admin has been told. When they let you in, you'll get an email and this page will open
          the app by itself.
        </p>
      </div>
      <Button variant="outline" onClick={signOut}>
        Sign out
      </Button>
    </AuthShell>
  )
}
